<template>
  <div class="field-palette">
    <!-- Tabs -->
    <div class="palette-tabs">
      <button
        v-if="!customOnly"
        class="tab-btn"
        :class="{ 'active': activeTab === 'library' }"
        @click="activeTab = 'library'"
      >
        <i class="bx bx-library"></i>
        Biblioteca
      </button>
      <button
        class="tab-btn"
        :class="{ 'active': activeTab === 'custom' }"
        @click="activeTab = 'custom'"
      >
        <i class="bx bx-plus-circle"></i>
        Customizado
      </button>
    </div>

    <!-- Search -->
    <div class="palette-search">
      <i class="bx bx-search"></i>
      <input
        v-model="search"
        type="text"
        :placeholder="activeTab === 'library' ? 'Buscar campos...' : 'Buscar tipos...'"
      />
      <button v-if="search" class="clear-search" @click="search = ''">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Library Tab Content -->
    <div v-if="activeTab === 'library'" class="palette-content">
      <div v-if="loadingLibrary" class="loading-state">
        <b-spinner small></b-spinner>
        <span>Carregando...</span>
      </div>

      <template v-else>
        <!-- Link to manage templates -->
        <router-link
          to="/form-builder/templates"
          class="manage-templates-link"
        >
          <i class="bx bx-cog"></i>
          <span>Gerenciar campos pré-prontos</span>
          <i class="bx bx-chevron-right"></i>
        </router-link>

        <!-- Registration Control Category -->
        <div v-if="registrationControlCategory" class="rc-section">
          <div
            class="category-header rc-header"
            :class="{ 'expanded': expandedRcCategory }"
            @click="expandedRcCategory = !expandedRcCategory"
          >
            <div class="category-info">
              <i class="bx bx-id-card"></i>
              <span>{{ registrationControlCategory.label }}</span>
            </div>
            <div class="category-meta">
              <span class="field-count">{{ registrationControlCategory.fields.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedRcCategory" class="category-fields">
            <div
              v-for="field in registrationControlCategory.fields"
              :key="field.id"
              class="field-item rc-item"
              :class="{ 'field-disabled': isRcFieldAlreadyUsed(field) }"
              @click="addRcField(field)"
            >
              <div class="field-icon rc-icon">
                <i :class="field.icon || 'bx bx-id-card'"></i>
              </div>
              <div class="field-info">
                <span class="field-name">
                  {{ field.label }}
                  <span v-if="isRcFieldAlreadyUsed(field)" class="used-badge">Ja inserido</span>
                  <span v-else class="rc-badge">Ctrl. Inscrição</span>
                </span>
                <span class="field-type">Somente leitura</span>
              </div>
              <button v-if="!isRcFieldAlreadyUsed(field)" class="btn-add" title="Adicionar campo">
                <i class="bx bx-plus"></i>
              </button>
              <i v-else class="bx bx-check used-check"></i>
            </div>
          </div>
        </div>

        <!-- Groups Section -->
        <div v-if="fieldGroups.length > 0 && !search" class="groups-section">
          <div
            class="category-header"
            :class="{ 'expanded': expandedGroups }"
            @click="expandedGroups = !expandedGroups"
          >
            <div class="category-info">
              <i class="bx bx-layer"></i>
              <span>Grupos de Campos</span>
            </div>
            <div class="category-meta">
              <span class="field-count">{{ fieldGroups.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedGroups" class="category-fields">
            <div
              v-for="group in fieldGroups"
              :key="group.key"
              class="field-item group-item"
              @click="addFieldGroup(group)"
            >
              <div class="field-icon group-icon">
                <i :class="group.icon || 'bx bx-group'"></i>
              </div>
              <div class="field-info">
                <span class="field-name">{{ group.label }}</span>
                <span class="field-type">{{ group.fields.length }} campos</span>
              </div>
              <button class="btn-add" title="Adicionar grupo">
                <i class="bx bx-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Category Accordion -->
        <div
          v-for="category in filteredLibraryCategories"
          :key="category.key"
          class="field-category"
        >
          <div
            class="category-header"
            :class="{ 'expanded': expandedCategories[category.key] }"
            @click="toggleCategory(category.key)"
          >
            <div class="category-info">
              <i :class="category.icon || 'bx bx-folder'"></i>
              <span>{{ category.label }}</span>
            </div>
            <div class="category-meta">
              <span class="field-count">{{ category.fields.length }}</span>
              <i class="bx bx-chevron-down expand-icon"></i>
            </div>
          </div>

          <div v-show="expandedCategories[category.key]" class="category-fields">
            <div
              v-for="field in category.fields"
              :key="field.id"
              class="field-item"
              :class="{ 'field-disabled': isStandardAlreadyUsed(field) }"
              :draggable="!isStandardAlreadyUsed(field)"
              @dragstart="!isStandardAlreadyUsed(field) && onDragStart($event, field, 'library')"
              @click="addLibraryField(field)"
            >
              <div class="field-icon">
                <i :class="field.icon || 'bx bx-text'"></i>
              </div>
              <div class="field-info">
                <span class="field-name">
                  {{ field.label }}
                  <span v-if="field.is_standard && !isStandardAlreadyUsed(field)" class="standard-badge">Padrao</span>
                  <span v-if="isStandardAlreadyUsed(field)" class="used-badge">Ja inserido</span>
                </span>
                <span v-if="field.help_text && !isStandardAlreadyUsed(field)" class="field-type">{{ field.help_text }}</span>
              </div>
              <button v-if="!isStandardAlreadyUsed(field)" class="btn-add" title="Adicionar campo">
                <i class="bx bx-plus"></i>
              </button>
              <i v-else class="bx bx-check used-check"></i>
            </div>
          </div>
        </div>

        <div v-if="filteredLibraryCategories.length === 0" class="empty-state">
          <i class="bx bx-search-alt"></i>
          <p>Nenhum campo encontrado</p>
        </div>
      </template>
    </div>

    <!-- Custom Tab Content -->
    <div v-else class="palette-content">
      <!-- Step 1: Select Field Format -->
      <div v-if="!customFieldForm.selectedType" class="custom-select-type">
        <div v-if="loadingFieldTypes" class="loading-state">
          <b-spinner small></b-spinner>
          <span>Carregando tipos...</span>
        </div>

        <template v-else>
          <div class="custom-intro">
            <p>Selecione o formato do campo:</p>
          </div>

          <div class="field-types-list">
            <div
              v-for="fieldType in filteredFieldFormats"
              :key="fieldType.id"
              class="field-item field-type-item"
              @click="selectFieldFormat(fieldType)"
            >
              <div class="field-icon">
                <i :class="getFieldTypeIcon(fieldType)"></i>
              </div>
              <div class="field-info">
                <span class="field-name">{{ fieldType.label }}</span>
                <span class="field-type-desc">{{ getCategoryLabel(fieldType.category) }}</span>
              </div>
              <i class="bx bx-chevron-right"></i>
            </div>
          </div>

          <div v-if="filteredFieldFormats.length === 0 && !loadingFieldTypes" class="empty-state">
            <i class="bx bx-search-alt"></i>
            <p>Nenhum formato encontrado</p>
          </div>
        </template>
      </div>

      <!-- Step 2: Configure Field -->
      <div v-else class="custom-configure-field">
        <div class="config-header">
          <button class="btn-back-config" @click="cancelCustomField">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="config-title">
            <i :class="getFieldIcon(customFieldForm.selectedType.name)"></i>
            <span>{{ customFieldForm.selectedType.label }}</span>
          </div>
        </div>

        <div class="config-form">
          <div class="form-group">
            <label>Label do Campo *</label>
            <input
              v-model="customFieldForm.label"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ex: Nome Completo"
            />
          </div>

          <div class="form-group">
            <label>Placeholder</label>
            <input
              v-model="customFieldForm.placeholder"
              type="text"
              class="form-control form-control-sm"
              placeholder="Texto de exemplo..."
            />
          </div>

          <div class="form-group">
            <label>Texto de Ajuda</label>
            <input
              v-model="customFieldForm.help_text"
              type="text"
              class="form-control form-control-sm"
              placeholder="Instruções para o usuário..."
            />
          </div>

          <!-- Options for select, radio, checkbox -->
          <div v-if="customFieldTypeHasOptions" class="form-group">
            <label>Opções</label>
            <div class="options-list">
              <div
                v-for="(opt, index) in customFieldForm.options"
                :key="index"
                class="option-item"
              >
                <input
                  v-model="opt.label"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Label"
                  @input="updateOptionValue(index)"
                />
                <button class="btn-remove-option" @click="removeOption(index)" v-if="customFieldForm.options.length > 1">
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <button class="btn-add-option" @click="addOption">
                <i class="bx bx-plus"></i> Adicionar opção
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="customFieldForm.required" />
              <span>Campo obrigatório</span>
            </label>
          </div>

          <div class="config-actions">
            <button class="btn-cancel" @click="cancelCustomField">
              Cancelar
            </button>
            <button
              class="btn-create"
              @click="createCustomField"
              :disabled="!customFieldForm.label"
            >
              <i class="bx bx-plus"></i>
              Adicionar Campo
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fieldLibrary } from '../fieldLibrary'
import { getFieldTemplatesGrouped, getFieldTypes, getFieldTemplateGroups, getRegistrationControlFields } from '../services'

export default {
  name: 'FieldPalette',
  props: {
    fieldTypesGrouped: {
      type: [Object, Array],
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    existingFields: {
      type: Array,
      default: () => ([])
    },
    steps: {
      type: Array,
      default: () => ([])
    },
    selectedStep: {
      type: Object,
      default: null
    },
    settings: {
      type: Object,
      default: () => ({})
    },
    customOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: this.customOnly ? 'custom' : 'library',
      search: '',
      loadingLibrary: false,
      loadingFieldTypes: false,
      apiTemplates: null, // Templates from API
      fieldTypes: [], // Field types from database
      fieldGroups: [], // Field groups from API
      rcFields: [], // Registration Control fields from API
      expandedRcCategory: true,
      expandedGroups: true,
      expandedCategories: {},
      expandedCustomCategories: {
        basic: true,
        selection: false,
        brazilian: false,
        datetime: false,
        special: false,
        layout: false
      },
      // Custom field creation form
      customFieldForm: {
        selectedType: null,
        label: '',
        placeholder: '',
        help_text: '',
        required: false,
        options: [{ label: '', value: '' }]
      },
      categoryLabels: {
        standard: 'Campos Padrão',
        basic: 'Campos Básicos',
        selection: 'Campos de Escolha',
        brazilian: 'Campos Brasileiros',
        datetime: 'Data e Hora',
        special: 'Campos Avançados',
        layout: 'Elementos de Layout'
      },
      fieldIcons: {
        name: 'bx bx-user',
        badge_name: 'bx bx-id-card',
        birth_date: 'bx bx-cake',
        city: 'bx bx-buildings',
        text: 'bx bx-text',
        textarea: 'bx bx-align-left',
        email: 'bx bx-envelope',
        phone: 'bx bx-phone',
        number: 'bx bx-hash',
        password: 'bx bx-lock',
        hidden: 'bx bx-hide',
        select: 'bx bx-chevron-down',
        multiselect: 'bx bx-list-check',
        checkbox: 'bx bx-checkbox-checked',
        radio: 'bx bx-radio-circle-marked',
        toggle: 'bx bx-toggle-right',
        cpf: 'bx bx-id-card',
        cnpj: 'bx bx-building',
        cpf_cnpj: 'bx bx-id-card',
        cep: 'bx bx-map-pin',
        state: 'bx bx-map',
        rg: 'bx bx-id-card',
        date: 'bx bx-calendar',
        time: 'bx bx-time',
        datetime: 'bx bx-calendar-event',
        daterange: 'bx bx-calendar-week',
        birthdate: 'bx bx-cake',
        file: 'bx bx-upload',
        image: 'bx bx-image',
        signature: 'bx bx-pen',
        facial: 'bx bx-face',
        rating: 'bx bx-star',
        slider: 'bx bx-slider',
        color: 'bx bx-palette',
        terms: 'bx bx-check-shield',
        heading: 'bx bx-heading',
        paragraph: 'bx bx-paragraph',
        divider: 'bx bx-minus',
        spacer: 'bx bx-move-vertical',
        alert: 'bx bx-info-circle',
        group: 'bx bx-group'
      },
      fieldTypeDescriptions: {
        name: 'Nome completo do participante',
        badge_name: 'Nome para impressão no crachá',
        birth_date: 'Data de nascimento com cálculo de idade',
        city: 'Cidade do participante',
        text: 'Campo de texto simples',
        textarea: 'Área de texto multilinha',
        email: 'Campo de e-mail com validação',
        phone: 'Telefone com máscara',
        number: 'Apenas números',
        password: 'Senha oculta',
        hidden: 'Campo invisível',
        select: 'Lista suspensa',
        multiselect: 'Seleção múltipla',
        checkbox: 'Caixa de marcação',
        radio: 'Escolha única',
        toggle: 'Interruptor liga/desliga',
        cpf: 'CPF com máscara e validação',
        cnpj: 'CNPJ com máscara e validação',
        cpf_cnpj: 'CPF ou CNPJ',
        cep: 'CEP com busca automática',
        state: 'Estados do Brasil',
        rg: 'RG com máscara',
        date: 'Seletor de data',
        time: 'Seletor de hora',
        datetime: 'Data e hora',
        daterange: 'Período de datas',
        birthdate: 'Data de nascimento',
        file: 'Upload de arquivo',
        image: 'Upload de imagem',
        signature: 'Assinatura digital',
        facial: 'Reconhecimento facial',
        rating: 'Avaliação com estrelas',
        slider: 'Controle deslizante',
        color: 'Seletor de cor',
        terms: 'Aceite de termos',
        heading: 'Título/Cabeçalho',
        paragraph: 'Texto informativo',
        divider: 'Linha divisória',
        spacer: 'Espaçamento',
        alert: 'Caixa de alerta',
        group: 'Grupo de campos'
      },
      categoryOrder: ['standard', 'basic', 'selection', 'brazilian', 'datetime', 'special', 'layout']
    }
  },
  computed: {
    // Library categories with fields (from API or fallback)
    libraryCategories () {
      let categories = []

      // If we have API data, use it as base
      if (this.apiTemplates) {
        categories = Object.keys(this.apiTemplates).map(key => ({
          key,
          label: this.apiTemplates[key].label || key,
          icon: this.apiTemplates[key].icon || 'bx bx-folder',
          fields: this.apiTemplates[key].templates || this.apiTemplates[key].fields || []
        }))
      } else {
        // Fallback to hardcoded library
        categories = Object.keys(fieldLibrary).map(key => ({
          key,
          label: fieldLibrary[key].label,
          icon: fieldLibrary[key].icon,
          fields: fieldLibrary[key].fields
        }))
      }

      // Always merge local-only categories (e.g. "custom") that are not in the API
      const existingKeys = new Set(categories.map(c => c.key))
      Object.keys(fieldLibrary).forEach(key => {
        if (!existingKeys.has(key)) {
          categories.push({
            key,
            label: fieldLibrary[key].label,
            icon: fieldLibrary[key].icon,
            fields: fieldLibrary[key].fields
          })
        }
      })

      return categories
    },

    filteredLibraryCategories () {
      if (!this.search) {
        return this.libraryCategories
      }
      const searchLower = this.search.toLowerCase()
      return this.libraryCategories
        .map(cat => ({
          ...cat,
          fields: cat.fields.filter(f =>
            f.label.toLowerCase().includes(searchLower) ||
            (f.name && f.name.toLowerCase().includes(searchLower))
          )
        }))
        .filter(cat => cat.fields.length > 0)
    },

    // Custom field types
    normalizedTypes () {
      if (this.fieldTypesGrouped && !Array.isArray(this.fieldTypesGrouped)) {
        return this.fieldTypesGrouped
      }
      if (Array.isArray(this.fieldTypesGrouped)) {
        const grouped = {}
        this.fieldTypesGrouped.forEach(type => {
          const category = type.category || 'basic'
          if (!grouped[category]) {
            grouped[category] = []
          }
          grouped[category].push(type)
        })
        return grouped
      }
      return {}
    },

    filteredFieldTypes () {
      const filtered = {}
      const searchLower = this.search.toLowerCase()

      this.categoryOrder.forEach(category => {
        const types = this.normalizedTypes[category]
        if (types && types.length > 0) {
          const filteredBySearch = searchLower
            ? types.filter(t =>
              t.label.toLowerCase().includes(searchLower) ||
              t.name.toLowerCase().includes(searchLower)
            )
            : types

          if (filteredBySearch.length > 0) {
            filtered[category] = filteredBySearch
          }
        }
      })

      return filtered
    },

    customFieldTypeHasOptions () {
      if (!this.customFieldForm.selectedType) return false
      // Use has_options from database, fallback to name check
      if (typeof this.customFieldForm.selectedType.has_options === 'boolean') {
        return this.customFieldForm.selectedType.has_options
      }
      const optionTypes = ['select', 'multiselect', 'radio', 'checkbox']
      return optionTypes.includes(this.customFieldForm.selectedType.name)
    },

    // All field types in a flat list (no categories)
    allFieldTypes () {
      const types = []
      if (this.fieldTypesGrouped && typeof this.fieldTypesGrouped === 'object') {
        // If it's grouped by category, flatten it
        if (!Array.isArray(this.fieldTypesGrouped)) {
          Object.values(this.fieldTypesGrouped).forEach(categoryTypes => {
            if (Array.isArray(categoryTypes)) {
              types.push(...categoryTypes)
            }
          })
        } else {
          // If it's already an array
          types.push(...this.fieldTypesGrouped)
        }
      }
      return types
    },

    filteredAllFieldTypes () {
      if (!this.search) {
        return this.allFieldTypes
      }
      const searchLower = this.search.toLowerCase()
      return this.allFieldTypes.filter(t =>
        t.label.toLowerCase().includes(searchLower) ||
        t.name.toLowerCase().includes(searchLower)
      )
    },

    // Verifica se o step selecionado é de acompanhante
    isCompanionStep () {
      if (!this.selectedStep) return false
      return this.selectedStep.is_companion_step ||
        (this.selectedStep.name || '').startsWith('Acompanhante')
    },

    // IDs dos steps de acompanhante
    companionStepIds () {
      return this.steps
        .filter(s => s.is_companion_step || (s.name || '').startsWith('Acompanhante'))
        .map(s => String(s.id || s._tempId))
    },

    // field_type_ids já usados em steps NÃO-acompanhante
    usedFieldTypeIds () {
      const used = new Set()
      this.existingFields.forEach(f => {
        const stepId = String(f.step_id || '')
        const isInCompanionStep = this.companionStepIds.includes(stepId)
        if (!isInCompanionStep) {
          if (f.field_type_id) {
            used.add(Number(f.field_type_id))
          }
        }
      })
      return used
    },

    // Registration Control: é ativo?
    isRegistrationControlEnabled () {
      return this.settings &&
        this.settings.registration_control_enabled &&
        this.settings.registration_control_id
    },

    // Registration Control: categoria para a paleta
    registrationControlCategory () {
      if (!this.isRegistrationControlEnabled || this.rcFields.length === 0) return null

      const searchLower = this.search ? this.search.toLowerCase() : ''
      const fields = this.rcFields.map(rcField => ({
        id: 'rc_' + rcField.rc_field,
        label: rcField.label,
        name: 'rc_' + rcField.rc_field,
        fieldType: { name: rcField.type || 'text' },
        placeholder: '',
        required: false,
        disabled: true,
        readonly: true,
        category: 'registration_control',
        is_rc_field: true,
        rc_field: rcField.rc_field,
        rc_type: rcField.type || 'text',
        rc_field_type_id: rcField.field_type_id || null,
        rc_mask: rcField.mask || null,
        rc_options: rcField.options || null,
        icon: 'bx bx-id-card'
      }))

      const filtered = searchLower
        ? fields.filter(f => f.label.toLowerCase().includes(searchLower) || f.name.toLowerCase().includes(searchLower))
        : fields

      if (filtered.length === 0) return null

      return {
        key: 'registration_control',
        label: 'Controle de Inscrição',
        icon: 'bx bx-id-card',
        fields: filtered
      }
    },

    // Formatos de campo filtrados pela busca (do banco de dados)
    filteredFieldFormats () {
      if (!this.search) {
        return this.fieldTypes
      }
      const searchLower = this.search.toLowerCase()
      return this.fieldTypes.filter(f =>
        f.label.toLowerCase().includes(searchLower) ||
        f.name.toLowerCase().includes(searchLower)
      )
    }
  },
  watch: {
    'settings.registration_control_id' () {
      this.loadRegistrationControlFields()
    },
    'settings.registration_control_enabled' () {
      this.loadRegistrationControlFields()
    }
  },
  mounted () {
    this.initPalette()
  },
  methods: {
    initPalette () {
      this.loadTemplatesFromAPI()
      this.loadFieldTypesFromAPI()
      this.loadFieldGroups()
      this.loadRegistrationControlFields()
      // Initialize expanded state for library categories
      this.libraryCategories.forEach((cat, index) => {
        this.$set(this.expandedCategories, cat.key, index === 0)
      })
    },

    async loadTemplatesFromAPI () {
      this.loadingLibrary = true
      try {
        const data = await getFieldTemplatesGrouped()

        // Validate response - should be an object with category keys
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const keys = Object.keys(data)

          // Check if we have valid categories
          if (keys.length > 0) {
            this.apiTemplates = data
            // Update expanded state for API categories
            keys.forEach((key, index) => {
              this.$set(this.expandedCategories, key, index === 0)
            })
          }
        }
      } catch (err) {
        console.log('Using fallback library:', err.message)
        // Keep using hardcoded library as fallback
      } finally {
        this.loadingLibrary = false
      }
    },

    async loadRegistrationControlFields () {
      if (!this.isRegistrationControlEnabled) {
        this.rcFields = []
        return
      }
      try {
        const data = await getRegistrationControlFields(this.settings.registration_control_id)
        if (data && Array.isArray(data.data)) {
          this.rcFields = data.data
        } else if (data && Array.isArray(data)) {
          this.rcFields = data
        } else {
          this.rcFields = []
        }
      } catch (err) {
        console.log('RC fields not available:', err.message)
        this.rcFields = []
      }
    },

    isRcFieldAlreadyUsed (rcPaletteItem) {
      return this.existingFields.some(f =>
        f.config && f.config.source === 'registration_control' && f.config.rc_field === rcPaletteItem.rc_field
      )
    },

    addRcField (rcPaletteItem) {
      if (this.isRcFieldAlreadyUsed(rcPaletteItem)) return
      this.$emit('add-rc-field', rcPaletteItem)
    },

    async loadFieldGroups () {
      try {
        const data = await getFieldTemplateGroups()
        if (Array.isArray(data)) {
          this.fieldGroups = data
        }
      } catch (err) {
        console.log('Groups not available:', err.message)
        this.fieldGroups = []
      }
    },

    async loadFieldTypesFromAPI () {
      this.loadingFieldTypes = true
      try {
        const response = await getFieldTypes()
        // Response from service is already response.data from axios
        // Fractal returns { data: [...] }
        if (response && response.data && Array.isArray(response.data)) {
          this.fieldTypes = response.data
        } else if (Array.isArray(response)) {
          this.fieldTypes = response
        } else {
          this.fieldTypes = []
        }
      } catch (err) {
        console.error('Error loading field types:', err)
        this.fieldTypes = []
      } finally {
        this.loadingFieldTypes = false
      }
    },

    toggleCategory (key) {
      this.$set(this.expandedCategories, key, !this.expandedCategories[key])
    },

    toggleCustomCategory (key) {
      this.expandedCustomCategories[key] = !this.expandedCustomCategories[key]
    },

    getCategoryLabel (category) {
      return this.categoryLabels[category] || category.toUpperCase()
    },

    getFieldIcon (fieldTypeName) {
      return this.fieldIcons[fieldTypeName] || 'bx bx-text'
    },

    getFieldTypeIcon (fieldType) {
      // If field type has a boxicons icon, use it directly
      if (fieldType.icon && fieldType.icon.startsWith('bx')) {
        return fieldType.icon
      }
      // Otherwise, use our mapping based on the name
      return this.fieldIcons[fieldType.name] || 'bx bx-text'
    },

    getFieldTypeDescription (fieldTypeName) {
      return this.fieldTypeDescriptions[fieldTypeName] || ''
    },

    isStandardAlreadyUsed (field) {
      if (!field.is_standard) return false
      if (this.isCompanionStep) return false
      if (field.field_type_id) {
        return this.usedFieldTypeIds.has(Number(field.field_type_id))
      }
      return false
    },

    addLibraryField (field) {
      if (this.isStandardAlreadyUsed(field)) return
      this.$emit('add-library-field', field)
    },

    addFieldGroup (group) {
      this.$emit('add-field-group', group)
    },

    addCustomField (fieldType) {
      this.$emit('add-field', fieldType)
    },

    onDragStart (event, item, source) {
      const data = {
        source, // 'library' or 'type'
        item
      }
      event.dataTransfer.setData('application/json', JSON.stringify(data))
      event.dataTransfer.effectAllowed = 'copy'
    },

    // Custom field creation methods
    selectFieldFormat (format) {
      this.customFieldForm.selectedType = format
      this.customFieldForm.label = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    selectFieldType (fieldType) {
      // Legacy - redirect to selectFieldFormat
      this.selectFieldFormat(fieldType)
    },

    cancelCustomField () {
      this.customFieldForm.selectedType = null
      this.customFieldForm.label = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    createCustomField () {
      if (!this.customFieldForm.label) return

      const fieldType = this.customFieldForm.selectedType

      // Build options array if applicable
      let options = null
      if (this.customFieldTypeHasOptions) {
        options = this.customFieldForm.options
          .filter(opt => opt.label.trim())
          .map(opt => ({
            label: opt.label,
            value: opt.label
          }))
        if (options.length === 0) {
          options = [{ label: 'Opção 1', value: 'Opção 1' }]
        }
      }

      const customField = {
        field_type_id: fieldType.id, // ID do tipo de campo no banco
        format: fieldType.name, // O nome/formato do campo (text, select, etc.)
        formatLabel: fieldType.label,
        icon: fieldType.icon,
        component: fieldType.component,
        mask: fieldType.mask,
        label: this.customFieldForm.label,
        placeholder: this.customFieldForm.placeholder,
        help_text: this.customFieldForm.help_text,
        required: this.customFieldForm.required,
        options: options,
        config: fieldType.default_config,
        validations: fieldType.default_validations
      }

      this.$emit('add-field', customField)
      this.cancelCustomField()
    },

    addOption () {
      this.customFieldForm.options.push({ label: '', value: '' })
    },

    removeOption (index) {
      this.customFieldForm.options.splice(index, 1)
    },

    updateOptionValue (index) {
      const opt = this.customFieldForm.options[index]
      opt.value = opt.label
    },

    slugify (text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    }
  }
}
</script>

<style scoped>
.field-palette {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* Tabs */
.palette-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tab-btn:hover {
  color: #495057;
  background: #f8f9fa;
}

.tab-btn.active {
  color: #556ee6;
  border-bottom-color: #556ee6;
  background: transparent;
}

.tab-btn i {
  font-size: 16px;
}

/* Search */
.palette-search {
  padding: 12px 16px;
  position: relative;
  border-bottom: 1px solid #e9ecef;
}

.palette-search > i {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 16px;
}

.palette-search input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  background: #f8f9fa;
  transition: all 0.15s;
}

.palette-search input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.clear-search {
  position: absolute;
  right: 28px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  padding: 4px;
}

.clear-search:hover {
  color: #495057;
}

/* Content */
.palette-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.palette-content::-webkit-scrollbar {
  width: 6px;
}

.palette-content::-webkit-scrollbar-track {
  background: transparent;
}

.palette-content::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.palette-content::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Manage Templates Link */
.manage-templates-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #f0f4ff;
  border-radius: 8px;
  font-size: 12px;
  color: #556ee6;
  text-decoration: none;
  transition: all 0.15s;
}

.manage-templates-link:hover {
  background: #e0e7ff;
}

.manage-templates-link span {
  flex: 1;
}

.custom-intro {
  padding: 8px 12px;
  margin-bottom: 8px;
}

.custom-intro p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

/* Category */
.field-category {
  margin-bottom: 4px;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.category-header:hover {
  background: #f0f2f5;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.category-info i {
  font-size: 16px;
  color: #556ee6;
}

.category-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-count {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 10px;
}

.expand-icon {
  font-size: 18px;
  color: #adb5bd;
  transition: transform 0.2s;
}

.category-header.expanded .expand-icon {
  transform: rotate(180deg);
}

/* Fields */
.category-fields {
  padding: 8px 0 8px 8px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.field-item:last-child {
  margin-bottom: 0;
}

.field-item:hover {
  border-color: #556ee6;
  background: #f8f9ff;
  box-shadow: 0 2px 4px rgba(85, 110, 230, 0.1);
}

.field-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.field-icon i {
  font-size: 16px;
  color: #556ee6;
}

.field-info {
  flex: 1;
  min-width: 0;
}

.field-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.standard-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #556ee6;
  background: #e8edff;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.used-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  background: #e9ecef;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.field-disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.field-disabled:hover {
  border-color: #e9ecef !important;
  background: #f8f9fa !important;
  box-shadow: none !important;
}

.used-check {
  font-size: 18px;
  color: #34c38f;
  flex-shrink: 0;
}

.field-type {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.field-type-desc {
  display: block;
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
}

.btn-add {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}

.field-item:hover .btn-add {
  opacity: 1;
}

.btn-add:hover {
  background: #485ec4;
}

/* States */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #6c757d;
  font-size: 13px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-state i {
  font-size: 32px;
  color: #d0d5dd;
  margin-bottom: 8px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* Custom Field Configuration */
.custom-configure-field {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.btn-back-config {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back-config:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.config-title i {
  font-size: 18px;
  color: #556ee6;
}

.config-form {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.config-form::-webkit-scrollbar {
  width: 6px;
}

.config-form::-webkit-scrollbar-track {
  background: transparent;
}

.config-form::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.config-form::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.config-form .form-group {
  margin-bottom: 16px;
}

.config-form label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.config-form .form-control-sm {
  font-size: 13px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label span {
  font-size: 13px;
  color: #495057;
}

/* Options List */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-item .form-control-sm {
  flex: 1;
}

.btn-remove-option {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-remove-option:hover {
  background: #fecaca;
}

.btn-add-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-option:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Config Actions */
.config-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.btn-cancel {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel:hover {
  border-color: #d0d5dd;
  background: #f8f9fa;
}

.btn-create {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-create:hover:not(:disabled) {
  background: #485ec4;
}

.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Field type item chevron */
.field-type-item > i.bx-chevron-right {
  color: #adb5bd;
  font-size: 18px;
  flex-shrink: 0;
}

.field-type-item:hover > i.bx-chevron-right {
  color: #556ee6;
}

/* Groups Section */
.groups-section {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.group-item {
  border-color: #d4e0ff;
  background: #f8faff;
}

.group-item:hover {
  border-color: #556ee6;
  background: #eff3ff;
}

.group-icon {
  background: #e0e7ff;
}

.group-icon i {
  color: #4338ca;
}

/* Field types list (flat, no categories) */
.field-types-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-types-list .field-item {
  margin-bottom: 0;
}

/* Registration Control Section */
.rc-section {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.rc-header .category-info i {
  color: #e67e22;
}

.rc-item {
  border-color: #fde8d0;
  background: #fffaf5;
}

.rc-item:hover {
  border-color: #e67e22;
  background: #fff5eb;
}

.rc-icon {
  background: #fde8d0;
}

.rc-icon i {
  color: #d35400 !important;
}

.rc-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #e67e22;
  background: #fde8d0;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}
</style>
